<template>
  <b-container fluid>
    <v-wait :visible="loading">
      <template slot="waiting">
        <HalfCircleSpinner />
        <div class="spinner-message my-3 text-center">Loading...</div>
      </template>
      <template>
        <div class="mb-2 d-flex">
          <b-button v-if="prevRoute" :to="prevRoute" variant="v3_teal_outline">
            Back to Job Builder
          </b-button>
        </div>
        <b-row>
          <b-col md="4">
            <b-list-group>
              <b-list-group-item
                button
                @click="newJobInformationSet"
                class="d-flex align-items-center"
              >
                <i
                  class="mdi mdi-clipboard-plus-outline mr-2"
                  aria-hidden="true"
                />
                New Job Information Set
              </b-list-group-item>
              <template v-for="jIS in jobInformationSets">
                <b-list-group-item
                  button
                  :key="jIS.id"
                  @click="selectJobInformationSet(jIS.id)"
                  class="d-flex align-items-center"
                  :active="jobInformationSet && jIS.id === jobInformationSet.id"
                  active-class="btn-v3_teal_outline"
                >
                  <i
                    class="mdi mdi-clipboard-edit-outline mr-2"
                    aria-hidden="true"
                  />
                  {{ jIS.title || 'Unnamed Job Information Set' }}
                </b-list-group-item>
              </template>
            </b-list-group>
          </b-col>
          <b-col>
            <job-information-set-form
              v-if="jobInformationSet"
              :key="jobInformationSet.id || Date.now()"
              :job-information-set="jobInformationSet"
              :save-loading="saveLoading"
              :remove-loading="removeLoading"
              @save-job-information-set="saveJobInformationSet"
              @remove-job-information-set="removeJobInformationSet"
            />
            <template v-else>
              <p>
                This is the Job Information Sets manager. Here you can manage
                your dataset to populate the Job Information section of the Job
                Builder page.
              </p>
              <p>
                To get started, select "New Job Information Set". Fill in the
                fields and click the "Save" button. Your new set will be
                displayed on the left. This set will also appear in the
                drop-down list in the Job Information section of the Job Builder
                page.
              </p>
              <p>
                If you select an item with the name of your set in the drop-down
                list, then the data from this set will immediately fill all the
                fields.
              </p>
              <p>
                To make changes, select the desired set on the left. Make the
                necessary changes in the fields on the right and click the
                "Save" button.
              </p>
              <p>
                To remove, select the desired set on the left and click the
                "Remove" button on the right.
              </p>
            </template>
          </b-col>
        </b-row>
      </template>
    </v-wait>
  </b-container>
</template>

<script>
import Api from '@/providers/Api';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import { HalfCircleSpinner } from 'epic-spinners';
import JobInformationSetForm from '@/components/recruiter/job_information_set/JobInformationSetForm';

export default {
  name: 'JobInformationList',
  components: {
    HalfCircleSpinner,
    JobInformationSetForm,
  },
  data: function () {
    return {
      loading: false,
      jobInformationSets: [],
      jobInformationSet: null,
      saveLoading: false,
      prevRoute: null,
      removeLoading: false,
    };
  },
  created: function () {
    this.getJobInformationSets();
  },
  beforeRouteEnter: function (to, from, next) {
    next((vm) => (vm.prevRoute = from && from.path !== '/' && from.path));
  },
  methods: {
    getJobInformationSets: async function () {
      this.loading = true;
      try {
        this.jobInformationSets = await Api.getJobInformationSets();
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted, {
          duration: 6000,
        });
        this.jobInformationSets = [];
      } finally {
        this.loading = false;
      }
    },
    newJobInformationSet: function () {
      this.jobInformationSet = {
        title: '',
        job_title: '',
        description: '',
        welcome_message: '',
        tracking_num: '',
        external_url: '',
        company_name: '',
        location_zip_code: '',
      };
    },
    selectJobInformationSet: function (jISId) {
      const temp = (this.jobInformationSets || []).find(
        (jIS) => jIS.id === jISId
      );
      this.jobInformationSet = { ...temp };
    },
    saveJobInformationSet: async function (jIS) {
      this.saveLoading = true;
      try {
        let jobInformationSet = null;
        if (jIS.id) {
          jobInformationSet = await Api.updateJobInformationSet(jIS);
          const index = this.jobInformationSets.findIndex(
            (jIS) => jIS.id === jobInformationSet.id
          );
          this.$set(this.jobInformationSets, index, jobInformationSet);
        } else {
          jobInformationSet = await Api.createJobInformationSet(jIS);
          this.jobInformationSets.push(jobInformationSet);
        }

        this.jobInformationSet = this.jobInformationSets.find(
          (jIS) => jIS.id === jobInformationSet.id
        );
        this.$toasted.success(
          `Job Information Set ${jobInformationSet.title} successfully saved.`,
          { duration: 3000 }
        );
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted, {
          duration: 6000,
        });
      } finally {
        this.saveLoading = false;
      }
    },
    removeJobInformationSet: async function (jISId) {
      this.removeLoading = true;
      try {
        await Api.deleteJobInformationSet(jISId);
        this.jobInformationSet = null;
        const index = (this.jobInformationSets || []).findIndex(
          (jIS) => jIS.id === jISId
        );
        const title = this.jobInformationSets[index].title;
        this.$delete(this.jobInformationSets, index);
        this.$toasted.success(
          `Job Information Set ${title} successfully removed`,
          { duration: 3000 }
        );
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted, {
          duration: 6000,
        });
        this.jobInformationSet = null;
      } finally {
        this.removeLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../sass/v3';
.list-group-item {
  &.active {
    background-color: $v3_color_teal;
    color: $v3_color_white;
    border-color: $v3_color_teal;
  }
}
</style>
